import { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ProgressBar } from "@/components/ProgressBar";
import { CheckCircle2, Pencil, Trash2, Wheat, Droplets, Lightbulb, Thermometer } from "lucide-react";
import useTicker from "@/hooks/useTicker";
import {
  computeOrder,
  formatHoursAsDH,
  formatMinutes,
  formatNum,
  grainTypeMeta,
  TEMP_RANGE,
  HUMIDITY_RANGE,
} from "@/lib/growthLogic";

function StatTile({ label, value, sub, icon: Icon, accent = "bg-stone-50 border-stone-200", testId }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
          {label}
        </div>
        {Icon && <Icon size={14} className="text-stone-500" strokeWidth={1.75} />}
      </div>
      <div className="font-display text-xl sm:text-2xl font-medium text-stone-900 mt-1.5 tabular-nums">
        {value}
      </div>
      {sub && <div className="text-[11px] text-stone-500 mt-1 font-mono">{sub}</div>}
    </div>
  );
}

export function OrderDetailsDialog({ order, open, onOpenChange, onEdit, onDeliver, onDelete }) {
  const now = useTicker(60_000);
  const calc = useMemo(() => (order ? computeOrder(order, now) : null), [order, now]);

  if (!order || !calc) return null;

  const grain = grainTypeMeta(order.grain_type);
  const stage = calc.stage;
  const dayNum = stage.day;
  const stageBadge = calc.overdue ? "bg-rose-600 text-white border-rose-700" : stage.badge;

  const deliveryDate = new Date(
    new Date(order.start_datetime).getTime() + order.standard_cycle_days * 24 * 60 * 60 * 1000,
  );
  const deliveryLabel = deliveryDate.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });

  // Phase-specific water + light schedule
  const { phase, lightStatus, lightChangeIn, waterEveryH, nextWaterIn, dailyCycle } = calc.schedule;

  // accent classes per phase
  const phaseAccent = {
    soak:   "bg-amber-50 border-amber-100",
    dark:   "bg-slate-50 border-slate-200",
    growth: "bg-emerald-50 border-emerald-100",
    ready:  "bg-emerald-100 border-emerald-200",
  }[phase.key];

  // Elapsed (combined): "Day 2 · 36h elapsed"
  const elapsedHrs = Math.floor(calc.hoursInProduction);
  const elapsedMins = Math.floor((calc.hoursInProduction - elapsedHrs) * 60);
  const elapsedSub = `${elapsedHrs}h ${elapsedMins}m total · ${formatNum(calc.daysInProduction, 2)} days`;

  // Left (combined): "4d 12h left"
  const leftSub = calc.overdue
    ? `${formatHoursAsDH(-calc.hoursLeft)} overdue`
    : `${Math.max(0, calc.hoursLeft).toFixed(1)}h remaining`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[760px] max-h-[92vh] overflow-y-auto" data-testid="order-details-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-block w-2 h-2 rounded-full ${grain.dot}`} />
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
              {grain.label} · order
            </span>
          </div>
          <DialogTitle className="font-display text-2xl sm:text-3xl tracking-tight" data-testid="details-customer-name">
            {order.customer_name}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">
            Started {new Date(order.start_datetime).toLocaleString(undefined, {
              year: "numeric", month: "short", day: "numeric",
              hour: "2-digit", minute: "2-digit",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Phase banner */}
          <div className={`rounded-2xl px-4 py-3 border ${stageBadge}`} data-testid="details-phase-badge">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-80">
              Current phase
            </div>
            <div className="font-display text-base font-medium mt-0.5">
              {calc.overdue ? `Overdue · ${phase.label}` : `${phase.label} (${phase.range})`}
            </div>
          </div>

          {/* Progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-stone-500">
                Day {dayNum} of 7
              </div>
              <div className={`text-xs font-mono ${calc.overdue ? "text-rose-600" : "text-stone-700"}`}>
                {calc.overdue
                  ? `${formatHoursAsDH(-calc.hoursLeft)} overdue`
                  : `${formatHoursAsDH(calc.hoursLeft)} left`}
              </div>
            </div>
            <ProgressBar segmentsFilled={calc.segmentsFilled} overdue={calc.overdue} />
          </div>

          {/* Key stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatTile
              label="Day & Elapsed"
              value={`Day ${dayNum}`}
              sub={elapsedSub}
              accent="bg-emerald-50 border-emerald-100"
              testId="stat-day-elapsed"
            />
            <StatTile
              label="Time Left"
              value={
                <span className={calc.overdue ? "text-rose-600" : ""}>
                  {calc.overdue ? "Overdue" : formatHoursAsDH(calc.hoursLeft)}
                </span>
              }
              sub={leftSub}
              accent={calc.overdue ? "bg-rose-50 border-rose-100" : "bg-amber-50 border-amber-100"}
              testId="stat-time-left"
            />
            <StatTile
              label="Delivery Date"
              value={deliveryLabel}
              sub={`Cycle: ${order.standard_cycle_days}d / 168h`}
              accent={calc.overdue ? "bg-rose-50 border-rose-100" : "bg-emerald-50 border-emerald-100"}
              testId="stat-delivery-date"
            />

            <StatTile
              label="Water Schedule"
              value={
                waterEveryH
                  ? <span>Every <span className="font-mono">{waterEveryH}h</span></span>
                  : "—"
              }
              sub={
                nextWaterIn !== null && nextWaterIn !== undefined
                  ? `Next in ${formatMinutes(nextWaterIn)} · 1 min spray`
                  : phase.key === "soak"
                    ? "Soaking — no watering"
                    : "Harvest window"
              }
              icon={Droplets}
              accent={phaseAccent}
              testId="stat-water"
            />
            <StatTile
              label="Light Schedule"
              value={
                phase.key === "growth"
                  ? <span className="capitalize">{lightStatus}</span>
                  : <span>Off</span>
              }
              sub={
                phase.key === "growth"
                  ? `16h ON / 8h OFF · ${lightStatus === "on" ? "off" : "on"} in ${formatMinutes(lightChangeIn)}`
                  : phase.key === "dark"
                    ? "Lights OFF (dark phase)"
                    : phase.key === "soak"
                      ? "Lights OFF (soaking)"
                      : "Idle"
              }
              icon={Lightbulb}
              accent={phaseAccent}
              testId="stat-light"
            />
            <StatTile
              label="Temp & Humidity"
              value={
                <span className="font-mono text-base sm:text-lg">
                  {TEMP_RANGE.min}–{TEMP_RANGE.max}{TEMP_RANGE.unit}
                </span>
              }
              sub={`Humidity ${HUMIDITY_RANGE.min}–${HUMIDITY_RANGE.max}${HUMIDITY_RANGE.unit} · target ranges`}
              icon={Thermometer}
              accent="bg-sky-50 border-sky-100"
              testId="stat-temp-humidity"
            />

            <StatTile
              label="Grain Used"
              value={`${formatNum(calc.grainNeeded, 2)} kg`}
              accent="bg-amber-50 border-amber-100"
              testId="stat-grain-used"
            />
            <StatTile
              label="Grain Type"
              value={
                <span className="inline-flex items-center gap-2">
                  <Wheat size={16} className="text-stone-500" strokeWidth={1.75} />
                  {grain.label}
                </span>
              }
              accent={
                grain.value === "wheat"
                  ? "bg-yellow-50 border-yellow-100"
                  : grain.value === "corn"
                    ? "bg-orange-50 border-orange-100"
                    : "bg-amber-50 border-amber-100"
              }
              testId="stat-grain-type"
            />
            <StatTile
              label="Trays · Weight"
              value={
                <>
                  {Math.ceil(order.order_quantity_kg / order.target_weight_per_tray)}
                  <span className="text-stone-400 text-sm font-normal ml-1">trays</span>
                </>
              }
              sub={`Order: ${formatNum(order.order_quantity_kg, 2)} kg`}
              accent="bg-teal-50 border-teal-100"
              testId="stat-trays-weight"
            />
          </div>

          {/* Daily schedule summary line */}
          <div className="rounded-xl border border-stone-200 bg-white px-4 py-3 text-xs text-stone-600 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
              Today's schedule
            </span>
            <span className="text-stone-700">{dailyCycle}</span>
          </div>

          {order.notes && (
            <div className="rounded-2xl border border-stone-200 bg-white p-4">
              <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold mb-1.5">
                Notes
              </div>
              <div className="text-sm text-stone-700 whitespace-pre-wrap">{order.notes}</div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 flex-row">
          <Button
            variant="outline"
            data-testid="details-edit-btn"
            onClick={() => { onOpenChange(false); onEdit?.(order); }}
            className="gap-2"
          >
            <Pencil size={14} /> Edit
          </Button>
          <Button
            variant="outline"
            data-testid="details-delete-btn"
            onClick={() => { onOpenChange(false); onDelete?.(order); }}
            className="gap-2 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
          >
            <Trash2 size={14} /> Delete
          </Button>
          <div className="flex-1" />
          <Button
            data-testid="details-deliver-btn"
            onClick={() => { onOpenChange(false); onDeliver?.(order); }}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <CheckCircle2 size={15} /> Mark Delivered
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
